import { replaceDateTextWithRanges } from "@services/utils/dateMapper";
import { useTranslation } from "react-i18next";
import { FilterType } from "./useFilterConfig";

export interface CombinationsSelections {
  activities: string[];
  locations: string[];
  days: string[];
}

/**
 * Hook to build selection messages for filters
 * Handles both regular filters (single category) and combinations (multiple categories)
 */
export const useSelectionMessage = () => {
  const { t, i18n } = useTranslation();

  const formatItemsList = (items: string[]): string => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];

    const lang = i18n.language || "ca";
    const isCa = lang.startsWith("ca");
    const isEs = lang.startsWith("es");
    const conjunction = isCa ? "i" : isEs ? "y" : "and";

    const allButLast = items.slice(0, -1).join(", ");
    const last = items[items.length - 1];
    return `${allButLast} ${conjunction} ${last}`;
  };

  const buildRegularFilterMessage = (
    filterType: FilterType,
    selectedItems: string[],
    translationKey: string
  ): string => {
    const itemsText = formatItemsList(
      selectedItems.map((key) => t(`${translationKey}.${key}`))
    );

    switch (filterType) {
      case "activity": {
        // Separate activities into regular ones and those that need "locales de"
        const localesActivities = ["gastronomy", "nightlife"];
        const regularActivities: string[] = [];
        const localesItems: string[] = [];

        selectedItems.forEach((key) => {
          if (localesActivities.includes(key)) {
            localesItems.push(t(`${translationKey}.${key}`));
          } else {
            regularActivities.push(t(`${translationKey}.${key}`));
          }
        });

        const lang = i18n.language || "ca";
        const isCa = lang.startsWith("ca");
        const isEs = lang.startsWith("es");
        const conjunction = isCa ? "i" : isEs ? "y" : "and";

        const parts: string[] = [];

        if (regularActivities.length > 0) {
          const regularText = formatItemsList(regularActivities);
          parts.push(
            t("filter_section.proposals_part", {
              defaultValue: "propuestas de {{items}}",
              items: regularText
            })
          );
        }

        if (localesItems.length > 0) {
          const localesText = formatItemsList(localesItems);
          parts.push(
            t("filter_section.locales_part", {
              defaultValue: "locales de {{items}}",
              items: localesText
            })
          );
        }

        if (parts.length === 0) return "";

        const combinedParts = parts.join(` ${conjunction} `);
        return t("filter_section.selection_message", {
          defaultValue: "Quiero descubrir {{parts}}.",
          parts: combinedParts
        });
      }
      case "day":
        return t("filter_day.selection_message", {
          defaultValue: "Quiero ideas para hacer {{items}}.",
          items: itemsText
        });
      case "location":
        return t("filter_location.selection_message", {
          defaultValue: "Quiero buscar planes por zona: {{items}}.",
          items: itemsText
        });
      default:
        return itemsText;
    }
  };

  /**
   * Transforms a message by replacing date text with numeric date ranges
   * This should be called before sending the message to the backend
   *
   * @param message - The message to transform
   * @param filterType - Type of filter (only "day" filter will be transformed)
   * @param selectedItems - Array of selected item keys
   * @param translationKey - Translation key prefix for the filter type
   * @returns Transformed message with date text replaced by numeric ranges
   */
  const transformMessageForBackend = (
    message: string,
    filterType: FilterType,
    selectedItems: string[],
    translationKey: string
  ): string => {
    // Only transform if we have date selections
    if (filterType !== "day" || selectedItems.length === 0) {
      return message;
    }

    // Get the translated texts for the selected dates
    const translatedDateTexts = selectedItems.map((key) =>
      t(`${translationKey}.${key}`)
    );

    // Get current language for date formatting
    const lang = i18n.language || "ca";
    const languageCode = lang.substring(0, 2);

    // Replace date text with numeric ranges
    return replaceDateTextWithRanges(
      message,
      selectedItems,
      translatedDateTexts,
      languageCode
    );
  };

  return {
    buildRegularFilterMessage,
    transformMessageForBackend
  };
};
